import { NavLink, Outlet, useLocation } from "react-router";
import { useAuth } from "../auth/AuthContext.js";
import {
  HeartIcon,
  HomeIcon,
  PencilIcon,
  SettingsIcon,
} from "./icons.js";

/**
 * 应用外壳。
 *
 * 底部三个 tab：首页 / 纪念日 / 设置（spec §5.5、票 00 Q11：导航 4→3）。
 * 「回忆」被降级为首页里的入口（spec §7.3 未定形态），它**不是**一个 tab。
 *
 * 宽屏（≥1024px）把底部 tab 换成左侧栏 —— 同一套导航，两种摆法。这满足
 * 「响应式、优先适配手机」，而没有把原型 S6 的三栏工作台原样搬过来（那需要
 * 「列表 + 详情」同屏，属于 v1 之后的形态）。
 */
const TABS = [
  { to: "/", label: "首页", icon: HomeIcon, exact: true },
  { to: "/anniversaries", label: "纪念日", icon: HeartIcon, exact: false },
  { to: "/settings", label: "设置", icon: SettingsIcon, exact: false },
] as const;

function isActive(pathname: string, to: string, exact: boolean): boolean {
  if (exact) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function AppShell() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="brand-mark">
            <HeartIcon size={18} style={{ color: "#fff" }} />
          </span>
          <span>我们日记</span>
        </div>
        <NavLink to="/entries/new" className="btn btn-primary btn-sm">
          <PencilIcon size={15} />
          写日记
        </NavLink>
        <nav className="stack-sm" style={{ marginTop: 12 }}>
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={
                isActive(pathname, tab.to, tab.exact)
                  ? "sidenav-item on"
                  : "sidenav-item"
              }
            >
              <tab.icon size={17} />
              <span className="lb">{tab.label}</span>
            </NavLink>
          ))}
        </nav>
        <div style={{ flex: 1 }} />
        <div className="stack-sm" style={{ padding: "0 4px" }}>
          <div className="small ghost-text">{user?.username}</div>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            onClick={() => void logout()}
          >
            退出登录
          </button>
        </div>
      </aside>

      <div className="app-col">
        <main className="app-main">
          <Outlet />
        </main>

        <nav className="tabbar" aria-label="主导航">
          <div className="tabbar-pill">
            {TABS.map((tab) => {
              const active = isActive(pathname, tab.to, tab.exact);
              return (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  className={active ? "tab-item on" : "tab-item"}
                  aria-current={active ? "page" : undefined}
                >
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}
